import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  MenuItem,
  Menu,
  ListItemText,
  Drawer,
  Modal,
  Badge,
  Chip,
  Button,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AxiosResponse } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { MoreVert } from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ParticipantFilterModal from "./Modals/ParticipantFilterModal";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import { ModalBaseStyles, ModalHeader } from "../Common/styles/modal";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { fetchParticipantsList } from "../../Redux/actions/participantsAction";
import {
  appendParticipantIds,
  clearParticipantIds,
  setParticipantsLoader,
  setParticipantSort,
  setParticipantsPage,
  setParticipantToggle,
  appendAccessibleParticipants,
  patchParticipantFilter,
  clearParticipantFilterField,
  clearAllParticipantFilters,
} from "../../Redux/reducers/participantsSlice";
import { hasStudyListFilters } from "../../utils/participantFilterActive";
import { hasStudyDrawerFilters } from "../../utils/participantDrawerFilters";
import { useStudyParticipantFilterOptions } from "../../hooks/useStudyParticipantFilterOptions";
import { StudyFilterContext } from "../../utils/studyColumnFilters";
import ProgressDetailModal from "./Modals/ProgressDetailModal";
// import ParticipantArchiveModal from "./Modals/ParticipantArchiveModal";
import { featuresMap } from "../../utils/features";
// import SurveyProgressModal from "./Modals/SurveyprogressModal";
import ConfirmWithdrawModal from "./Modals/ConfirmWithdrawModal";
import ChangeGroupModal from "./ParticipantGroups/Modals/ChangeGroupModal";
import SettingsIcon from "@mui/icons-material/Settings";
import AddCustomFieldDrawer from "./ParticipantGroups/Drawers/AddCustomFieldDrawer";
import { permissions } from "../../utils/roles";
import { canMutateStudyParticipantTagsNotes } from "../../utils/participantLock";
import ViewNoteDetailsModal from "./ParticipantNotes/ViewNoteDetailsModal";
import AddNoteModal from "./ParticipantNotes/AddNoteModal";
import FreezeColumnsPopover from "../Common/UI/FreezeColumnsPopover";
import ParticipantTableLayout from "../Common/UI/ParticipantTableLayout";
import ParticipantListFooter from "../Common/UI/ParticipantListFooter";
import ParticipantFieldSearch from "./ParticipantFieldSearch";

import { useFrozenColumns } from "../../hooks/useFrozenColumns";
import { useStickyColumnOffsets } from "../../hooks/useStickyColumnOffsets";
import { orderColumnsForDisplay, getLastLeftPinnedColumnKey } from "../../utils/frozenColumns";
import {
  buildStudyParticipantColumns,
  getStudyColumnCellClassName,
  getStudyColumnCellSx,
} from "./studyParticipantTableColumns";

// const CustomBoxStyle: SxProps = {
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   borderRadius: 1,
//   height: "56px",
//   width: "56px",
//   border: "1px solid",
// };

type Props = {
  type: string;
};

export const getColorByStatus = (status: string) => {
  if (status === "completed") {
    return "#70AE71";
  } else if (status === "inprogress") {
    return "#F49D4C";
  } else if (status === "not_started") {
    return "#9CA3AF";
  } else {
    return "#9CA3AF";
  }
};

const ParticipantList = ({ type }: Props) => {
  const { id } = useParams();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const features = useAppSelector((state) => state.study.features);
  const sitePermissions = useAppSelector(
    (state) => state.study.sitePermissions
  );
  const selectedParticipantIds = useAppSelector(
    (state) => state.participants.selectedParticipantIds
  );

  // const [showArchive, setShowArchive] = useState<boolean>(false);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [showProgressModal, setShowProgressModal] = useState<string>("");
  const [showCustomFieldDrawer, setShowCustomFieldDrawer] = useState(false);

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [progressData, setProgressData] = useState<any[]>([]);

  const [showWithdrawConfirm, setShowWithdrawConfirm] =
    useState<boolean>(false);
  const [showChangeGroup, setShowChangeGroup] = useState<boolean>(false);
  const [changeGroupMode, setChangeGroupMode] = useState<"assign" | "change">(
    "change"
  );

  const [showTagsModal, setShowTagsModal] = useState<boolean>(false);
  const [selectedParticipantTags, setSelectedParticipantTags] = useState<
    string[]
  >([]);

  const [showNoteDetailsModal, setShowNoteDetailsModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const tableRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  const {
    loading,
    participantsData,
    totalParticipants,
    unfilteredTotal,
    paginationModel,
    toggleLoader,
    sortColumn,
    sortOrder,
    siteIds,
    status,
    isLocked,
    screenerCompleted,
    screenerReceived,
    screenerCompletedFrom,
    screenerCompletedTo,
    columns,
    cohortId,
    tagIds,
    customHeaderValues,
    notesQuery,
    searchField,
    searchQuery,
  } = useAppSelector((state) => state.participants);

  const sitesList = useAppSelector((state) => state.study.sitesList);
  const { cohortList, tagList } = useStudyParticipantFilterOptions(id);

  const filterState = useMemo(
    () => ({
      siteIds,
      status,
      isLocked,
      screenerCompleted: screenerCompleted || "",
      screenerReceived: screenerReceived || "",
      screenerCompletedFrom: screenerCompletedFrom || "",
      screenerCompletedTo: screenerCompletedTo || "",
      cohortId,
      tagIds,
      notesQuery,
      customHeaderValues,
    }),
    [
      siteIds,
      status,
      isLocked,
      screenerCompleted,
      screenerReceived,
      screenerCompletedFrom,
      screenerCompletedTo,
      cohortId,
      tagIds,
      notesQuery,
      customHeaderValues,
    ],
  );

  const filterContext: StudyFilterContext = useMemo(
    () => ({
      filterState,
      sitesList: sitesList || [],
      cohortList,
      tagList,
      sortColumn,
      sortOrder,
      onSort: (column, order) => {
        dispatch(setParticipantSort({ column, order }));
      },
      onPatch: (patch) => dispatch(patchParticipantFilter(patch)),
      onClearField: (field) => dispatch(clearParticipantFilterField(field)),
    }),
    [
      filterState,
      sitesList,
      cohortList,
      tagList,
      sortColumn,
      sortOrder,
      dispatch,
    ],
  );

  const hasActiveFilters = useMemo(
    () => hasStudyListFilters({ ...filterState, searchQuery }),
    [filterState, searchQuery],
  );

  const hasDrawerFilters = useMemo(
    () => hasStudyDrawerFilters(filterState),
    [filterState],
  );

  const studyPermissions = useAppSelector(
    (state) => state.study.studyPermissions
  );
  const dataEditable = useAppSelector((state) => state.study.dataEditable);
  const [canEdit] = useState(
    studyPermissions.includes(permissions.studySettings) && dataEditable
  );

  useEffect(() => {
    return () => {
      dispatch(clearParticipantIds());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchParticipantsList(
        paginationModel.page,
        sortOrder,
        sortColumn,
        siteIds,
        status,
        isLocked,
        screenerCompleted || "",
        screenerReceived || "",
        screenerCompletedFrom || "",
        screenerCompletedTo || "",
        cohortId,
        tagIds,
        customHeaderValues,
        notesQuery,
        searchField,
        searchQuery,
        id
      )
    );
  }, [
    type,
    toggleLoader,
    paginationModel,
    id,
    sitePermissions,
    sortOrder,
    sortColumn,
    siteIds,
    status,
    isLocked,
    screenerCompleted,
    screenerReceived,
    screenerCompletedFrom,
    screenerCompletedTo,
    cohortId,
    tagIds,
    customHeaderValues,
    notesQuery,
    searchField,
    searchQuery,
    dispatch,
  ]);

  const withdrawClick = (row: any) => {
    setSelectedRow(row);
    setShowWithdrawConfirm(true);
  };

  const closeWithdrawModal = () => {
    setShowWithdrawConfirm(false);
    setSelectedRow(null);
  };

  const changeGroupClick = (row: any, mode: "assign" | "change") => {
    setSelectedRow(row);
    setChangeGroupMode(mode);
    setShowChangeGroup(true);
  };

  const closeChangeGroupModal = () => {
    setShowChangeGroup(false);
    setSelectedRow(null);
  };

  const handleShowTags = (tags: string[]) => {
    setSelectedParticipantTags(tags);
    setShowTagsModal(true);
  };

  const closeTagsModal = () => {
    setShowTagsModal(false);
    setSelectedParticipantTags([]);
  };

  useEffect(() => {
    const container = tableRef.current;

    const updateShadowClasses = () => {
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;

      if (scrollLeft > 0) {
        if (!container.classList.contains("shadow-left")) {
          container.classList.add("shadow-left");
        }
      } else if (container.classList.contains("shadow-left")) {
        container.classList.remove("shadow-left");
      }

      if (scrollLeft + clientWidth < scrollWidth - 1) {
        if (!container.classList.contains("shadow-right")) {
          container.classList.add("shadow-right");
        }
      } else if (container.classList.contains("shadow-right")) {
        container.classList.remove("shadow-right");
      }
    };

    if (container) {
      updateShadowClasses();

      container.addEventListener("scrollend", updateShadowClasses);
      window.addEventListener("resize", updateShadowClasses);
    }

    return () => {
      if (container) {
        container.removeEventListener("scrollend", updateShadowClasses);
      }
      window.removeEventListener("resize", updateShadowClasses);
    };
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleNavigate = (row: any) => {
    if (!row?.isArchived) {
      navigate(
        `/studies/${id}/participants/${row?.id}/responses?participant=${row.displayId}`
      );
    }
  };

  const handleShowNoteDetails = (row: any) => {
    setSelectedRow(row);
    setShowNoteDetailsModal(true);
  };

  const handleActionClick = useCallback(
    async (row: any, action: string) => {
      try {
        let url: string = ``;
        dispatch(setParticipantsLoader(true));
        if (action === "lockUnlock") {
          url = `/participants/${row?.id}/${row?.isLocked ? "unlock" : "lock"
            }?studyId=${id}`;
        } else if (action === "unarchive") {
          url = `/participants/${row?.id}/unarchive`;
        }
        let res: AxiosResponse = await http.post(
          url,
          action === "unarchive" ? { studyId: id } : undefined
        );
        handleMenuClose();
        toastMessage("success", res?.data?.message);

        dispatch(setParticipantToggle());
      } catch (err) {
        dispatch(setParticipantsLoader(false));
        errorToastMessage(err as Error);
      }
    },
    [id, dispatch]
  );

  const columnDefinitions = useMemo(
    () =>
      buildStudyParticipantColumns({
        features,
        columns,
        dispatch,
        handleNavigate,
        handleShowTags,
        handleActionClick,
        handleShowNoteDetails,
        handleMenuClick,
        loading,
        dataEditable,
        filterContext,
      }),
    [
      features,
      columns,
      dispatch,
      handleNavigate,
      handleShowTags,
      handleActionClick,
      handleShowNoteDetails,
      handleMenuClick,
      loading,
      dataEditable,
      filterContext,
    ],
  );

  // const handleProgressCellClick = (data: any, type: string) => {
  //   setShowProgressModal(type);
  //   setProgressData(data);
  // };
  const closeProgressModal = () => {
    setShowProgressModal("");
    setProgressData([]);
  };

  // const archiveClick = (row: any) => {
  //   setSelectedRow(row?.id);
  //   setShowArchive(true);
  //   setAnchorEl(null);
  // };
  // const closeArchiveModal = () => {
  //   setShowArchive(false);
  //   setSelectedRow(null);
  // };

  const handlePageChange = (_1: any, newPage: number) => {
    dispatch(setParticipantsPage({ page: newPage, pageSize: pageSize }));
  };

  const openFilterModal = () => {
    setShowFilterModal(true);
  };
  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  const openCustomFieldDrawer = () => {
    setShowCustomFieldDrawer(true);
  };

  const closeCustomFieldDrawer = () => {
    setShowCustomFieldDrawer(false);
  };

  const handleCloseNoteDetailsModal = () => {
    setShowNoteDetailsModal(false);
    setSelectedRow(null);
  };

  const availableColumnKeys = useMemo(
    () =>
      columnDefinitions
        .filter((column) => column.freezable)
        .map((column) => column.key),
    [columnDefinitions],
  );

  const { frozenKeys, toggleFreeze, clearFrozen, isAtMax } = useFrozenColumns({
    listKey: "study",
    availableColumnKeys,
    columnsReady: !loading,
  });

  const { displayColumns, frozenKeysInOrder } = useMemo(
    () => orderColumnsForDisplay(columnDefinitions, frozenKeys),
    [columnDefinitions, frozenKeys],
  );

  const lastLeftPinnedKey = useMemo(
    () => getLastLeftPinnedColumnKey(displayColumns, frozenKeys),
    [displayColumns, frozenKeys],
  );

  const stickyLeft = useStickyColumnOffsets(
    frozenKeysInOrder,
    headerRefs,
    tableRef,
    `${participantsData?.length ?? 0}-${frozenKeysInOrder.join(",")}`,
    "select",
  );

  const freezableColumns = useMemo(
    () =>
      columnDefinitions
        .filter((column) => column.freezable)
        .map((column) => ({ key: column.key, label: column.label })),
    [columnDefinitions],
  );

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ParticipantTableLayout
        tableRef={tableRef}
        toolbar={
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <ParticipantFieldSearch />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <FreezeColumnsPopover
                columns={freezableColumns}
                frozenKeys={frozenKeys}
                isAtMax={isAtMax}
                onToggle={toggleFreeze}
                onClear={clearFrozen}
              />
              {hasDrawerFilters && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => dispatch(clearAllParticipantFilters())}
                >
                  Clear filters
                </Button>
              )}
              <IconButton
                onClick={openFilterModal}
                sx={{
                  height: "48px",
                  width: "48px",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                }}
              >
                <Badge
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#FF7164",
                    },
                  }}
                  overlap="circular"
                  invisible={!hasDrawerFilters}
                >
                  <FilterAltIcon
                    color={hasDrawerFilters ? "primary" : "disabled"}
                    sx={{ fontSize: "24px" }}
                  />
                </Badge>
              </IconButton>
              {canEdit && (
                <IconButton
                  onClick={openCustomFieldDrawer}
                  sx={{
                    height: "48px",
                    width: "48px",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "8px",
                  }}
                >
                  <SettingsIcon color={"primary"} sx={{ fontSize: "24px" }} />
                </IconButton>
              )}
            </Box>
          </Box>
        }
        footer={
          <ParticipantListFooter
            filteredCount={totalParticipants}
            totalCount={unfilteredTotal}
            hasActiveFilters={hasActiveFilters}
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            onPageChange={handlePageChange}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              {displayColumns.map((column) => (
                <StyledTableCell
                  key={column.key}
                  ref={(element) => {
                    headerRefs.current[column.key] =
                      element as HTMLTableCellElement | null;
                  }}
                  align={column.align}
                  padding={column.padding}
                  sx={getStudyColumnCellSx(
                    column,
                    frozenKeys,
                    frozenKeysInOrder,
                    stickyLeft,
                    true,
                  )}
                  className={getStudyColumnCellClassName(
                    column,
                    frozenKeys,
                    lastLeftPinnedKey,
                  )}
                >
                  {column.renderHeader()}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          {!loading && totalParticipants > 0 && (
            <TableBody>
              {participantsData?.map((row: any) => {
                const checked = !!selectedParticipantIds.find(
                  (c) => c === row.id
                );

                return (
                  <TableRow key={row?.id}>
                    {displayColumns.map((column) => (
                      <StyledTableCell
                        key={column.key}
                        align={column.align}
                        padding={column.padding}
                        sx={{
                          ...getStudyColumnCellSx(
                            column,
                            frozenKeys,
                            frozenKeysInOrder,
                            stickyLeft,
                            false,
                          ),
                          ...(column.key === "participantId"
                            ? { cursor: "pointer" }
                            : {}),
                          ...(column.key === "status"
                            ? { textTransform: "capitalize" }
                            : {}),
                        }}
                        className={getStudyColumnCellClassName(
                          column,
                          frozenKeys,
                          lastLeftPinnedKey,
                        )}
                        onClick={
                          column.key === "participantId"
                            ? () => handleNavigate(row)
                            : column.key === "select"
                              ? (e) => e.stopPropagation()
                              : undefined
                        }
                      >
                        {column.renderCell(row, checked)}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
        {!loading && totalParticipants === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </ParticipantTableLayout>
      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        {/* <MenuItem
          onClick={() => {
            if (selectedRow?.isArchived) {
              handleActionClick(selectedRow, "unarchive");
            } else {
              archiveClick(selectedRow);
            }
            setAnchorEl(null);
          }}
          disabled={loading || !selectedRow?.canArchive}
        >
          <ListItemText>
            {selectedRow?.isArchived ? "Un-Archive" : "Archive"}
          </ListItemText>
        </MenuItem> */}
        <MenuItem
          onClick={() => {
            handleActionClick(selectedRow, "lockUnlock");
            setAnchorEl(null);
          }}
          disabled={
            loading ||
            !selectedRow?.canLock ||
            selectedRow?.isArchived ||
            !dataEditable
          }
        >
          <ListItemText>
            {selectedRow?.isLocked ? "Unlock" : "Lock"}
          </ListItemText>
        </MenuItem>
        <MenuItem
          disabled={
            loading ||
            selectedRow?.isArchived ||
            selectedRow?.isLocked ||
            !selectedRow?.canWithdraw ||
            selectedRow?.status === "withdrawn" ||
            !dataEditable
          }
          onClick={() => {
            withdrawClick(selectedRow);
            setAnchorEl(null);
          }}
        >
          <ListItemText>Withdraw</ListItemText>
        </MenuItem>
        {features[featuresMap.participantGroups] && (
          <MenuItem
            disabled={
              loading ||
              selectedRow?.isArchived ||
              selectedRow?.isLocked ||
              selectedRow?.status === "withdrawn" ||
              !dataEditable
            }
            onClick={() => {
              const isUngrouped =
                !selectedRow?.cohort || selectedRow.cohort === "-";
              changeGroupClick(selectedRow, isUngrouped ? "assign" : "change");
              setAnchorEl(null);
            }}
          >
            <ListItemText>
              {!selectedRow?.cohort || selectedRow.cohort === "-"
                ? "Assign Group"
                : "Change Group"}
            </ListItemText>
          </MenuItem>
        )}
        {/* {features?.[featuresMap.wearable] && (
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate(
                  `/studies/${id}/participants/${selectedRow?.id}/analysis`
                );
              }}
            >
              <ListItemText>View Analysis</ListItemText>
            </MenuItem>
          )} */}

        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            setShowAddNoteModal(true);
          }}
          disabled={
            loading ||
            !canMutateStudyParticipantTagsNotes({
              hasEditPermission: true,
              dataEditable,
              isLocked: selectedRow?.isLocked,
              isArchived: selectedRow?.isArchived,
              isExcluded: selectedRow?.status === "excluded",
              isWithdrawn: selectedRow?.status === "withdrawn",
            })
          }
        >
          <ListItemText>Add Notes</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate(
              `/studies/${id}/participants/${selectedRow?.id}/details?participant=${selectedRow?.participantId}`
            );
          }}
        >
          <ListItemText>View Details</ListItemText>
        </MenuItem>
      </Menu>

      {/* {showArchive && (
        <ParticipantArchiveModal
          openModal={showArchive}
          closeModal={closeArchiveModal}
          participantId={selectedRow}
        />
      )} */}
      {showProgressModal === "visit" && (
        <ProgressDetailModal
          showModal={!!showProgressModal}
          closeModal={closeProgressModal}
          data={progressData}
        />
      )}
      {/* {showProgressModal === "survey" && (
          <SurveyProgressModal
            showModal={!!showProgressModal}
            closeModal={closeProgressModal}
            data={progressData}
          />
        )} */}
      {showWithdrawConfirm && (
        <ConfirmWithdrawModal
          openModal={showWithdrawConfirm}
          closeModal={closeWithdrawModal}
          participantId={selectedRow?.id}
        />
      )}
      {showChangeGroup && selectedRow && (
        <ChangeGroupModal
          openModal={showChangeGroup}
          participant={selectedRow}
          mode={changeGroupMode}
          closeModal={closeChangeGroupModal}
          refreshPage={() => dispatch(setParticipantToggle())}
        />
      )}
      {showAddNoteModal && (
        <AddNoteModal
          showModal={showAddNoteModal}
          closeModal={() => setShowAddNoteModal(false)}
          refreshPage={() => setShowAddNoteModal(false)}
          participantIdOverride={selectedRow?.id}
        />
      )}
      <Drawer
        open={showFilterModal}
        onClose={closeFilterModal}
        anchor="right"
      >
        <ParticipantFilterModal closeModal={closeFilterModal} studyId={id} />
      </Drawer>
      <Drawer
        open={showCustomFieldDrawer}
        onClose={closeCustomFieldDrawer}
        anchor="right"
      >
        <AddCustomFieldDrawer
          closeDrawer={closeCustomFieldDrawer}
        // selectedFields={selectedFields}
        // setSelectedFields={setSelectedFields}
        />
      </Drawer>
      {showTagsModal && (
        <Modal open={showTagsModal} onClose={closeTagsModal}>
          <Box sx={{ ...ModalBaseStyles, minHeight: "10vh" }}>
            <ModalHeader title={"Tags"} onCloseClick={closeTagsModal} />
            <Box>
              {selectedParticipantTags?.map((tag, index) => (
                <Chip
                  sx={{
                    backgroundColor: "#F6F1F8",
                    paddingX: "10px",
                    paddingY: "2px",
                    borderRadius: "6px",
                    fontSize: 10,
                    fontWeight: 500,
                    color: "primary.main",
                    m: 0.5,
                  }}
                  key={index}
                  label={tag}
                />
              ))}
            </Box>
          </Box>
        </Modal>
      )}
      {showNoteDetailsModal && selectedRow && (
        <ViewNoteDetailsModal
          open={showNoteDetailsModal}
          onClose={handleCloseNoteDetailsModal}
          data={selectedRow}
          studyId={id}
        />
      )}
      {showFilterModal && (
        <Drawer
          open={showFilterModal}
          onClose={closeFilterModal}
          anchor="right"
        >
          <ParticipantFilterModal
            closeModal={closeFilterModal}
            studyId={id}
          />
        </Drawer>
      )}
    </Box>
  );
};

export default ParticipantList;
